import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, LogIn, Eye, EyeOff } from 'lucide-react';
import { invitationApi } from '@/lib/api/invitationApi';
import { InvitationInfo } from '@/lib/models/invitation';
import { useAuth } from '@/contexts/AuthContext';
import { ActionButton } from '@/components/custom-ui/action-button';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LegalConsent } from '@/components/legal/LegalConsent';

const InvitationAcceptPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { tokens, isLoading: authLoading } = useAuth();
    const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAccepting, setIsAccepting] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // #355 — employer-kind invitations set a password instead of accepting.
    const [password, setPassword] = useState('');
    // #324 — consent, for the employer-activation branch only. Accepting an
    // invitation as an EXISTING signed-in candidate is not account creation and
    // is left alone.
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [activated, setActivated] = useState(false);

    useEffect(() => {
        if (token) loadInvitation(token);
    }, [token]);

    const loadInvitation = async (t: string) => {
        setIsLoading(true);
        try {
            const data = await invitationApi.getInvitationByToken(t);
            setInvitation(data);
        } catch {
            setError('Inbjudan hittades inte eller är ogiltig.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!token) return;
        setIsAccepting(true);
        setError(null);
        try {
            await invitationApi.acceptInvitation(token);
            setAccepted(true);
        } catch (err: any) {
            setError(err?.message || 'Kunde inte acceptera inbjudan.');
        } finally {
            setIsAccepting(false);
        }
    };

    // #355 — an admin-created employer has no password yet, so this branch runs
    // entirely pre-login. It must never require `tokens`.
    const isEmployerInvite = invitation?.kind === 'employer';

    const handleActivate = async () => {
        if (!token) return;
        if (password.length < 8) {
            setError('Lösenordet måste vara minst 8 tecken.');
            return;
        }
        // #324 — this branch IS account activation: the employer is choosing
        // their first password and becoming a user. Their organisation being
        // invited by an admin is not the individual accepting the terms, so this
        // path gets the same gate as candidate signup.
        if (!acceptedTerms) {
            setShowConsentError(true);
            setError('Du behöver godkänna villkoren för att aktivera kontot.');
            return;
        }
        setIsAccepting(true);
        setError(null);
        try {
            await invitationApi.activateEmployerInvitation(token, password);
            setActivated(true);
        } catch (err: any) {
            setError(err?.message || 'Kunde inte spara lösenordet.');
        } finally {
            setIsAccepting(false);
        }
    };

    const workplace = [invitation?.organisation_name, invitation?.unit_name]
        .filter(Boolean)
        .join(' — ');

    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <LoadingState message="Laddar inbjudan..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
            <div className="bg-white rounded-2xl border-2 border-gray-100 w-full max-w-md p-8 text-center">
                {error && !invitation ? (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Ogiltig inbjudan</h1>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <button onClick={() => navigate('/')} className="text-teal-600 font-medium hover:underline">
                            Gå till startsidan
                        </button>
                    </>
                ) : activated ? (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Lösenordet är sparat</h1>
                        <p className="text-gray-600 mb-6">Du kan nu logga in med <strong>{invitation?.email}</strong>.</p>
                        <ActionButton icon={LogIn} onClick={() => navigate('/')}>
                            Logga in
                        </ActionButton>
                    </>
                ) : accepted ? (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Inbjudan accepterad!</h1>
                        <p className="text-gray-600 mb-6">Välkommen till Nexilink.</p>
                        <ActionButton onClick={() => navigate('/dashboard')}>
                            Gå till Dashboard
                        </ActionButton>
                    </>
                ) : invitation?.is_expired ? (
                    <>
                        <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-amber-600" />
                        </div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Inbjudan har gått ut</h1>
                        <p className="text-gray-600 mb-6">Kontakta din arbetsgivare för att få en ny inbjudan.</p>
                    </>
                ) : invitation?.status !== 'pending' ? (
                    <>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Inbjudan redan {invitation?.status === 'accepted' ? 'accepterad' : 'hanterad'}</h1>
                        <button onClick={() => navigate('/')} className="text-teal-600 font-medium hover:underline mt-4">
                            Gå till startsidan
                        </button>
                    </>
                ) : isEmployerInvite ? (
                    /* #355 — the employer's account already exists; all that is
                       missing is a password. No login required, and deliberately
                       BEFORE the !tokens branch: this person cannot log in yet. */
                    <>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Välj ditt lösenord</h1>
                        <p className="text-gray-600 mb-1">
                            Ett arbetsgivarkonto har skapats åt dig
                            {workplace ? <> för <strong>{workplace}</strong></> : null}.
                        </p>
                        <p className="text-gray-600 mb-6">
                            E-post: <strong>{invitation?.email}</strong>
                        </p>
                        <div className="text-left space-y-2 mb-4">
                            <Label htmlFor="new-password">Lösenord (minst 8 tecken)</Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && password.length >= 8 && !isAccepting) {
                                            handleActivate();
                                        }
                                    }}
                                    placeholder="••••••••"
                                    autoFocus
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* #324 — the Enter key above also submits, and handleActivate
                            gates on this, so pressing Enter without ticking is
                            refused the same way the button is. */}
                        <div className="text-left mb-4">
                            <LegalConsent
                                id="invite-legal-consent"
                                checked={acceptedTerms}
                                onChange={setAcceptedTerms}
                                invalid={showConsentError && !acceptedTerms}
                                disabled={isAccepting}
                            />
                        </div>
                        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                        <ActionButton onClick={handleActivate} disabled={isAccepting || password.length < 8}>
                            {isAccepting ? 'Sparar...' : 'Spara och fortsätt'}
                        </ActionButton>
                    </>
                ) : !tokens ? (
                    <>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Du har blivit inbjuden!</h1>
                        <p className="text-gray-600 mb-2">E-post: <strong>{invitation?.email}</strong></p>
                        <p className="text-gray-600 mb-6">Logga in eller skapa ett konto för att acceptera inbjudan.</p>
                        <ActionButton icon={LogIn} onClick={() => navigate(`/?redirect=/accept-invitation/${token}`)}>
                            Logga in / Skapa konto
                        </ActionButton>
                    </>
                ) : (
                    <>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Acceptera inbjudan</h1>
                        <p className="text-gray-600 mb-6">Du har blivit inbjuden att gå med på Nexilink.</p>
                        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                        <ActionButton onClick={handleAccept} disabled={isAccepting}>
                            {isAccepting ? 'Accepterar...' : 'Acceptera inbjudan'}
                        </ActionButton>
                    </>
                )}
            </div>
        </div>
    );
};

export default InvitationAcceptPage;
